import { toast, Tooltip } from '@affine/component';
import { useTrashModalHelper } from '@affine/core/components/hooks/affine/use-trash-modal-helper';
import { useAllBlockSuiteDocMeta } from '@affine/core/components/hooks/use-all-block-suite-page-meta';
import { CollectionService } from '@affine/core/modules/collection';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import type { Tag } from '@affine/core/modules/tag';
import { useActionDocsWiki, useGetDocsWiki, useWikiStatus } from '@affine/core/modules/wiki';
import type { Collection, Filter } from '@affine/env/filter';
import { Trans, useI18n } from '@affine/i18n';
import type { DocMeta } from '@blocksuite/store';
import type { PostDocsStatusBodyAction } from '@docnboard/rest';
import { useLiveData, useService, WorkspaceService } from '@toeverything/infra';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { FloatingToolbar } from '../components/floating-toolbar';
import { ListFloatingToolbar } from '../components/list-floating-toolbar';
import { usePageItemGroupDefinitions } from '../group-definitions';
import { usePageHeaderColsDef } from '../header-col-def';
import { PageOperationCell } from '../operation-cell';
import { useWikiActionsConfig } from '../operation-menu-items/wiki-action/use-wiki-action-config';
import { PageListItemRenderer } from '../page-group';
import { ListTableHeader } from '../page-header';
import type { DocMetaWithWiki, ItemListHandle, ListItem } from '../types';
import { useFilteredPageMetas } from '../use-filtered-page-metas';
import { VirtualizedList } from '../virtualized-list';
import {
  CollectionPageListHeader,
  PageListHeader,
  TagPageListHeader,
} from './page-list-header';

const usePageOperationsRenderer = () => {
  const t = useI18n();
  const collectionService = useService(CollectionService);
  const removeFromAllowList = useCallback(
    (id: string) => {
      collectionService.deletePagesFromCollections([id]);
      toast(t['com.affine.collection.removePage.success']());
    },
    [collectionService, t]
  );
  const pageOperationsRenderer = useCallback(
    (page: DocMetaWithWiki, isInAllowList?: boolean) => {
      return (
        <PageOperationCell
          page={page}
          isInAllowList={isInAllowList}
          onRemoveFromAllowList={() => removeFromAllowList(page.id)}
        />
      );
    },
    [removeFromAllowList]
  );
  return pageOperationsRenderer;
};

export const VirtualizedPageList = ({
  tag,
  collection,
  filters,
  listItem,
  setHideHeaderCreateNewPage,
}: {
  tag?: Tag;
  collection?: Collection;
  filters?: Filter[];
  listItem?: DocMeta[];
  setHideHeaderCreateNewPage?: (hide: boolean) => void;
}) => {
  // DNB_FIX получить isReadOnly
  const isReadOnly = useLiveData(
    useService(WorkspacePermissionService).permission.isReadOnly$
  );

  const listRef = useRef<ItemListHandle>(null);
  const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const currentWorkspace = useService(WorkspaceService).workspace;
  const pageMetas = useAllBlockSuiteDocMeta(currentWorkspace.docCollection);
  const pageOperations = usePageOperationsRenderer();
  const pageHeaderColsDef = usePageHeaderColsDef();

  const filteredPageMetas = useFilteredPageMetas(pageMetas, {
    filters,
    collection,
  });

  // DNB_FIX объединить данные о документе с информацией из БЗ
  const { wikiDocsMap } = useGetDocsWiki(currentWorkspace.id);
  const wikiEnabled = useWikiStatus({workspaceId: currentWorkspace.id});
  const { onActionDocs, isPending } = useActionDocsWiki(currentWorkspace.id);

  const pageMetasToRender = useMemo(() => {
    const sourceDocs = listItem ?? filteredPageMetas;

    return sourceDocs.map(doc => ({ ...doc, ...wikiDocsMap.get(doc.id) }));
  }, [filteredPageMetas, listItem, wikiDocsMap]);

  const filteredSelectedPageIds = useMemo(() => {
    const ids = pageMetasToRender.map(page => page.id);
    return selectedPageIds.filter(id => ids.includes(id));
  }, [pageMetasToRender, selectedPageIds]);

  const hideFloatingToolbar = useCallback(() => {
    listRef.current?.toggleSelectable();
  }, []);

  const onAction = useCallback(
    (actionType: PostDocsStatusBodyAction) => {
      onActionDocs(filteredSelectedPageIds, actionType);
    },
    [onActionDocs, filteredSelectedPageIds]
  );

  const pageOperationRenderer = useCallback(
    (item: ListItem) => {
      const page = item as DocMetaWithWiki;
      const isInAllowList = collection?.allowList?.includes(page.id);
      // DNB_FIX скрывать меню, если isReadOnly
      return !isReadOnly ? pageOperations(page, isInAllowList) : <div></div>;
    },
    [collection, pageOperations, isReadOnly]
  );

  const pageHeaderRenderer = useCallback(() => {
    return <ListTableHeader headerCols={pageHeaderColsDef} />;
  }, [pageHeaderColsDef]);

  const pageItemRenderer = useCallback((item: ListItem) => {
    return <PageListItemRenderer {...item} />;
  }, []);

  const heading = useMemo(() => {
    if (tag) {
      return <TagPageListHeader workspaceId={currentWorkspace.id} tag={tag} />;
    }
    if (collection) {
      return (
        <CollectionPageListHeader
          workspaceId={currentWorkspace.id}
          collection={collection}
        />
      );
    }
    return <PageListHeader />;
  }, [collection, currentWorkspace.id, tag]);

  const { setTrashModal } = useTrashModalHelper();

  const handleMultiDelete = useCallback(() => {
    if (filteredSelectedPageIds.length === 0) {
      return;
    }
    const pageNameMapping = Object.fromEntries(
      pageMetas.map(meta => [meta.id, meta.title])
    );

    const pageNames = filteredSelectedPageIds.map(
      id => pageNameMapping[id] ?? ''
    );
    setTrashModal({
      open: true,
      pageIds: filteredSelectedPageIds,
      pageTitles: pageNames,
    });
    hideFloatingToolbar();
  }, [filteredSelectedPageIds, hideFloatingToolbar, pageMetas, setTrashModal]);

  const group = usePageItemGroupDefinitions();

  return (
    <>
      <VirtualizedList
        ref={listRef}
        selectable="toggle"
        draggable
        atTopThreshold={80}
        atTopStateChange={setHideHeaderCreateNewPage}
        onSelectionActiveChange={setShowFloatingToolbar}
        heading={heading}
        groupBy={group}
        selectedIds={filteredSelectedPageIds}
        onSelectedIdsChange={setSelectedPageIds}
        items={pageMetasToRender}
        rowAsLink
        docCollection={currentWorkspace.docCollection}
        operationsRenderer={pageOperationRenderer}
        itemRenderer={pageItemRenderer}
        headerRenderer={pageHeaderRenderer}
      />
      <ListFloatingToolbar
        open={showFloatingToolbar}
        onDelete={handleMultiDelete}
        onClose={hideFloatingToolbar}
        content={
          <Trans
            i18nKey="com.affine.page.toolbar.selected"
            count={filteredSelectedPageIds.length}
          >
            <div style={{ color: 'var(--affine-text-secondary-color)' }}>
              {{ count: filteredSelectedPageIds.length } as any}
            </div>
            selected
          </Trans>
        }
        actions={
          wikiEnabled.data?.data?.enable && <ListToolbarWikiActions onAction={onAction} isPending={isPending} />
        }
      />
    </>
  );
};

// DNB_FIX добавить кнопки взаимодействия с БЗ
type ListToolbarActionsProps = {
  onAction: (action: PostDocsStatusBodyAction) => void;
  isPending: boolean;
};

const ListToolbarWikiActions: React.FC<ListToolbarActionsProps> = ({
  onAction,
  isPending,
}) => {
  const actions = useWikiActionsConfig();
  return (
    <>
      {actions
        .filter(a => a.type !== 'delete')
        .map(action => (
          <React.Fragment key={action.type}>
            <Tooltip content={action.label}>
              <span>
                <FloatingToolbar.Button
                  onClick={() => onAction(action.type)}
                  icon={action.icon}
                  type={action.variant === 'error' ? 'danger' : 'default'}
                  data-testid={action.testId}
                  loading={isPending}
                />
              </span>
            </Tooltip>

            <FloatingToolbar.Separator />
          </React.Fragment>
        ))}
    </>
  );
};
